import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Onboarding.css';

const storageKey = (id) => `marksure.tour.${id}.done`;

const tourSeen = (id) => {
    try { return localStorage.getItem(storageKey(id)) === '1'; } catch { return true; }
};

const markTourSeen = (id) => {
    try { localStorage.setItem(storageKey(id), '1'); } catch { /* private mode: the tour simply shows again */ }
};

/**
 * Opens a tour automatically the first time `ready` is true, and lets a "Take the tour"
 * button reopen it later. Seen state is kept per browser.
 */
export function useFeatureTour(id, ready = true) {
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!ready || tourSeen(id)) return undefined;
        // Let the page finish laying out before measuring the first target.
        const timer = setTimeout(() => setOpen(true), 600);
        return () => clearTimeout(timer);
    }, [id, ready]);
    const close = useCallback(() => { markTourSeen(id); setOpen(false); }, [id]);
    const start = useCallback(() => setOpen(true), []);
    return { open, start, close };
}

const findTarget = (selector) => {
    if (!selector) return null;
    const element = document.querySelector(selector);
    if (!element) return null;
    const rect = element.getBoundingClientRect();
    return rect.width || rect.height ? element : null;
};

/** Step-by-step spotlight tour. Steps whose target isn't on the page are skipped. */
export default function FeatureTour({ steps, open, onClose }) {
    const [index, setIndex] = useState(0);
    const [rect, setRect] = useState(null);
    const [visibleSteps, setVisibleSteps] = useState([]);
    const nextRef = useRef(null);
    const step = visibleSteps[Math.min(index, visibleSteps.length - 1)];

    // Decide which steps apply after the page has rendered, so elements that appear in the
    // same update that opened the tour (e.g. an expanded form) are found.
    useLayoutEffect(() => {
        if (!open) { setVisibleSteps([]); return; }
        setIndex(0);
        setVisibleSteps(steps.filter((item) => !item.target || findTarget(item.target)));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const target = step?.target;
    useLayoutEffect(() => {
        if (!open || !step) return undefined;
        const element = findTarget(target);
        if (!element) { setRect(null); return undefined; }
        element.scrollIntoView({ block: 'center', behavior: 'smooth' });
        let frame = 0;
        const measure = () => {
            cancelAnimationFrame(frame);
            frame = requestAnimationFrame(() => setRect(element.getBoundingClientRect()));
        };
        measure();
        const settle = setTimeout(measure, 400);
        window.addEventListener('scroll', measure, true);
        window.addEventListener('resize', measure);
        return () => {
            clearTimeout(settle);
            cancelAnimationFrame(frame);
            window.removeEventListener('scroll', measure, true);
            window.removeEventListener('resize', measure);
        };
        // Re-measure only when the step changes, not on every parent render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, index, target]);

    const last = index >= visibleSteps.length - 1;
    const next = useCallback(() => (last ? onClose() : setIndex((value) => value + 1)), [last, onClose]);
    const back = useCallback(() => setIndex((value) => Math.max(0, value - 1)), []);

    useEffect(() => {
        if (!open) return undefined;
        nextRef.current?.focus({ preventScroll: true });
        const onKey = (event) => {
            if (event.key === 'Escape') onClose();
            else if (event.key === 'ArrowRight') next();
            else if (event.key === 'ArrowLeft') back();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, index, next, back, onClose]);

    if (!open || !step) return null;

    const pad = 8;
    const spotlight = rect && {
        top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2,
    };
    // Card below the target when it fits, otherwise above; centred when there is no target.
    const cardWidth = Math.min(360, window.innerWidth - 32);
    let cardStyle = { width: cardWidth };
    if (spotlight) {
        const left = Math.min(Math.max(16, spotlight.left), window.innerWidth - cardWidth - 16);
        const below = spotlight.top + spotlight.height + 14;
        cardStyle = below + 230 < window.innerHeight
            ? { ...cardStyle, left, top: below }
            : { ...cardStyle, left, bottom: Math.max(16, window.innerHeight - spotlight.top + 14) };
    }

    return createPortal(
        <div className="tour-layer">
            <div className="tour-blocker" onClick={(event) => event.stopPropagation()} />
            {spotlight
                ? <div className="tour-spotlight" style={spotlight} aria-hidden="true" />
                : <div className="tour-dim" aria-hidden="true" />}
            <div className={`tour-card${spotlight ? '' : ' is-centred'}`} style={cardStyle} role="dialog" aria-modal="true" aria-labelledby="tour-title" aria-describedby="tour-body">
                <div className="tour-progress" aria-label={`Step ${index + 1} of ${visibleSteps.length}`}>
                    {visibleSteps.map((item, position) => <span key={item.title} className={position <= index ? 'is-reached' : ''} />)}
                </div>
                <h2 id="tour-title">{step.title}</h2>
                <p id="tour-body">{step.body}</p>
                <div className="tour-actions">
                    <button type="button" className="tour-skip" onClick={onClose}>{last ? 'Close' : 'Skip tour'}</button>
                    <span className="tour-count">{index + 1} / {visibleSteps.length}</span>
                    {index > 0 && <button type="button" className="tour-back" onClick={back}>Back</button>}
                    <button type="button" ref={nextRef} className="tour-next" onClick={next}>{last ? 'Got it' : 'Next'}</button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

/** Small "Take the tour" trigger used in page headers. */
export function TourButton({ onClick, children = 'Take the tour' }) {
    return (
        <button type="button" className="tour-launch" onClick={onClick}>
            <span aria-hidden="true">▶</span> {children}
        </button>
    );
}
